import { api } from "./api";

export type InvoiceStatus = "draft" | "sent" | "partial" | "paid" | "overdue" | "void";
export type PayableStatus = "pending_approval" | "approved" | "rejected" | "paid";
export type PayableKind = "hotel" | "vehicle" | "driver" | "attraction" | "other";
export type QBConnectionStatus = "not_configured" | "connected" | "error" | "lagging";

// W5 · FinanceOverview (/_overview)
export interface FinanceOverview {
  total_revenue_mtd_cents: number;
  ar_outstanding_cents: number;
  ap_outstanding_cents: number;
  cash_position_cents: number;
  invoices_total: number;
  invoices_overdue: number;
  qb_connection_status: QBConnectionStatus;
  qb_last_sync_at: string | null;
  qb_last_error: string | null;
  recent_invoices: Invoice[];
}

export interface QBSyncResult {
  triggered: boolean;
  status: "mock" | "not_configured" | "queued";
  message: string;
  queued_invoice_count: number;
  next_step_hint: string;
}

export interface FinanceDashboard {
  pending_invoices_count: number;
  ar_total_cents: number;
  ar_overdue_cents: number;
  ap_due_this_week_cents: number;
  qb_sync_status: "ok" | "lagging" | "error" | "disabled";
  qb_last_sync_at: string | null;
  cashflow_30d: { date: string; in_cents: number; out_cents: number }[];
}

export interface Receivable {
  id: string;
  invoice_no: string;
  order_no: string | null;
  source: "b2b" | "direct" | "api";
  customer: string;
  settlement_terms: string | null;
  issued_at: string;
  due_at: string;
  amount_cents: number;
  currency: string;
  paid_cents: number;
  status: InvoiceStatus;
  days_overdue: number;
}

export interface Payable {
  id: string;
  kind: PayableKind;
  supplier: string;
  invoice_ref: string;
  amount_cents: number;
  currency: string;
  payment_terms: string | null;
  detail: string | null;
  due_at: string;
  status: PayableStatus;
  submitted_by: string;
  submitted_at: string;
  decided_by: string | null;
  decided_at: string | null;
}

export interface Invoice {
  id: string;
  invoice_no: string;
  order_no: string | null;
  customer: string;
  customer_kind: "b2b" | "direct" | "api";
  amount_cents: number;
  paid_cents: number;
  status: InvoiceStatus;
  issued_at: string;
  due_at: string;
  line_items: unknown[];
  qb_doc_id: string | null;
  qb_synced_at: string | null;
  created_at: string;
}

export interface InvoiceCreate {
  customer: string;
  customer_kind: "b2b" | "direct" | "api";
  order_no?: string | null;
  amount_cents: number;
  currency?: string;
  memo?: string | null;
  payment_terms?: string | null;
  issued_at: string;
  due_at: string;
  line_items?: unknown[];
}

export interface PLReport {
  period: string;
  revenue_cents: number;
  cost_cents: number;
  gross_profit_cents: number;
  operating_expenses_cents: number | null;
  net_profit_cents: number | null;
  gross_margin_pct?: number | null;   // 后端算·毛利率
  net_margin_pct?: number | null;     // 后端算·净利率（经理遮罩 → null）
  by_channel: { channel: string; revenue_cents: number; profit_cents: number }[];
  margin_trend: { label: string; gross_margin_pct: number; net_margin_pct: number }[];
  _masked?: boolean;
}

export interface Cashflow {
  days: { date: string; in_cents: number; out_cents: number; net_cents: number }[];
  total_in_cents: number;
  total_out_cents: number;
  net_cents: number;
  monthly: { label: string; in_cents: number; out_cents: number; net_cents: number }[];
  avg_in_cents: number;
  yoy_pct: number;
}

export interface PLLine { label: string; cents: number; pct: number }
export interface CFLine { label: string; cents: number }
export interface FullReport {
  pl: {
    period: string;
    income_lines: PLLine[]; revenue_cents: number;
    cost_lines: PLLine[]; cost_cents: number;
    gross_profit_cents: number; gross_margin_pct: number;
    opex_lines: PLLine[]; opex_cents: number;
    net_profit_cents: number; net_margin_pct: number;
  };
  channels: { label: string; markup_pct: number; net_cents: number }[];
  product_ranking: { name: string; groups: string; net_cents: number }[];
  cashflow: {
    operating: CFLine[]; op_subtotal_cents: number;
    investing: CFLine[]; inv_subtotal_cents: number;
    financing: CFLine[]; fin_subtotal_cents: number;
    net_cents: number; opening_cents: number; closing_cents: number;
  };
  fx: {
    usd: { rev_cents: number; exp_cents: number; net_cents: number };
    cny: { rev_cny: number; exp_cny: number; net_cny: number; net_usd_equiv_cents: number };
    rate: number; source: string; fx_gain_cents: number;
  };
}

export const PAYABLE_KIND_LABEL: Record<PayableKind, string> = {
  hotel: "酒店",
  vehicle: "车辆",
  driver: "司导",
  attraction: "景点",
  other: "其他",
};

export const INVOICE_STATUS_LABEL: Record<InvoiceStatus, string> = {
  draft: "草稿",
  sent: "已发出",
  partial: "部分付款",
  paid: "已付清",
  overdue: "逾期",
  void: "作废",
};

export const PAYABLE_STATUS_LABEL: Record<PayableStatus, string> = {
  pending_approval: "待审",
  approved: "已批",
  rejected: "已拒",
  paid: "已付",
};

export const financeApi = {
  // W5 · period=YYYY-MM 选月份（demo：当月真实·过去月缩放）
  overview: (period?: string) => api<FinanceOverview>(`/api/v1/finance/_overview${period ? `?period=${period}` : ""}`),
  getInvoice: (iid: string) => api<Invoice>(`/api/v1/finance/invoices/${iid}`),
  qbSync: () => api<QBSyncResult>("/api/v1/finance/qb-sync", { method: "POST" }),

  dashboard: () => api<FinanceDashboard>("/api/v1/finance/dashboard"),
  receivables: (source?: "b2b" | "direct" | "api") =>
    api<Receivable[]>(`/api/v1/finance/receivables${source ? `?source=${source}` : ""}`),
  payables: (kind?: PayableKind) =>
    api<Payable[]>(`/api/v1/finance/payables${kind ? `?kind=${kind}` : ""}`),
  submitPayableApproval: (pid: string, note?: string) =>
    api<Payable>(`/api/v1/finance/payables/${pid}/submit-approval`, {
      method: "POST",
      body: JSON.stringify({ note }),
    }),
  // REASON-2 · 原因放请求体：拒绝必填、批准选填
  decidePayable: (pid: string, approve: boolean, reason?: { reason_code?: string; reason?: string }) =>
    api<Payable>(`/api/v1/finance/payables/${pid}/approve?approve=${approve}`, { method: "PATCH", body: JSON.stringify(reason ?? {}) }),

  listInvoices: (status?: InvoiceStatus) =>
    api<Invoice[]>(`/api/v1/finance/invoices${status ? `?invoice_status=${status}` : ""}`),
  createInvoice: (payload: InvoiceCreate) =>
    api<Invoice>("/api/v1/finance/invoices", { method: "POST", body: JSON.stringify(payload) }),
  updateInvoice: (iid: string, payload: Partial<InvoiceCreate> & { status?: InvoiceStatus }) =>
    api<Invoice>(`/api/v1/finance/invoices/${iid}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  markPaid: (
    iid: string,
    payload: { amount_cents: number; paid_at: string; method?: string; note?: string },
  ) =>
    api<Invoice>(`/api/v1/finance/invoices/${iid}/mark-paid`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  plReport: (period?: string) =>
    api<PLReport>(`/api/v1/finance/reports/pl${period ? `?period=${period}` : ""}`),
  cashflow: (period?: string) => api<Cashflow>(`/api/v1/finance/reports/cashflow${period ? `?period=${period}` : ""}`),
  fullReport: (period?: string) => api<FullReport>(`/api/v1/finance/reports/full${period ? `?period=${period}` : ""}`),
};

/**
 * 财务金额格式化（2026-06-22 用户拍板）：
 * - 币种感知：USD→$ / CNY→¥（B2B 同行收付款涉及人民币结算·报价仍 USD）
 * - 显示到分（2 位小数）：平台按百分比抽佣会产生分位，财务必须见分
 * 入参 cents = 该币种的「分」（整数）· currency 缺省 USD。
 */
export const formatCents = (cents: number | null | undefined, currency: string = "USD"): string => {
  if (cents == null) return "—";
  const amount = cents / 100;
  const locale = currency === "CNY" ? "zh-CN" : "en-US";
  return amount.toLocaleString(locale, { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
